/**
 * InterventionLibrarySelector Component
 * 
 * A categorized intervention selection dialog for treatment planning.
 * Features:
 * - Search across all interventions
 * - Category-based browsing with expandable sections
 * - Multiple selection support
 * - Custom intervention entry
 * - Modality and frequency settings per intervention
 */

import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible';
import { Search, ChevronDown, ChevronRight, Plus, X, Check, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Modality and Frequency options
const MODALITIES = [
  { value: 'Individual', label: 'Individual (1:1)' },
  { value: 'Group', label: 'Group Setting' },
  { value: 'Family', label: 'Family Session' },
  { value: 'Couples', label: 'Couples Session' },
  { value: 'Telehealth', label: 'Telehealth/Virtual' },
  { value: 'In-Home', label: 'In-Home Visit' },
];

const FREQUENCIES = [
  { value: 'daily', label: 'Daily' },
  { value: '2x/week', label: '2x per week' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'bi-weekly', label: 'Bi-weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'as-needed', label: 'As Needed' },
];

export const InterventionLibrarySelector = ({
  isOpen,
  onClose,
  onSave,
  selectedInterventions = [],
  title = "Select Interventions"
}) => {
  // State
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCategories, setExpandedCategories] = useState({});
  const [selected, setSelected] = useState(selectedInterventions);
  const [showCustomForm, setShowCustomForm] = useState(false);
  const [customName, setCustomName] = useState('');
  const [customDescription, setCustomDescription] = useState('');
  const [customModality, setCustomModality] = useState('');
  const [customFrequency, setCustomFrequency] = useState('');
  
  // Track previous isOpen value to detect open transitions
  const prevIsOpenRef = useRef(false);
  
  // Fetch intervention library when dialog opens (not on every render)
  useEffect(() => {
    const justOpened = isOpen && !prevIsOpenRef.current;
    
    // Only reset state when dialog transitions from closed to open
    if (justOpened) {
      fetchInterventions();
      setSelected(selectedInterventions);
      setSearchQuery('');
      setShowCustomForm(false);
      setCustomName('');
      setCustomDescription('');
      setCustomModality('');
      setCustomFrequency('');
    }
    
    // Update ref after checking
    prevIsOpenRef.current = isOpen;
  }, [isOpen]); // Only depend on isOpen, not selectedInterventions
  
  // Note: selectedInterventions is intentionally NOT in the dependency array
  // to prevent resetting user's work when parent re-renders

  const fetchInterventions = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/intervention-library/grouped`);
      setCategories(response.data || []);
      // Expand first category by default
      if (response.data?.length > 0) {
        setExpandedCategories({ [response.data[0].category.id]: true });
      }
    } catch (error) {
      console.error('Failed to fetch interventions:', error);
      toast.error('Failed to load intervention library');
    } finally {
      setLoading(false);
    }
  };

  // Filter interventions based on search
  const filteredCategories = useMemo(() => {
    if (!searchQuery.trim()) return categories;
    
    const query = searchQuery.toLowerCase();
    return categories
      .map(cat => ({
        ...cat,
        interventions: cat.interventions.filter(
          int => int.name.toLowerCase().includes(query) || 
                 int.description?.toLowerCase().includes(query)
        )
      }))
      .filter(cat => cat.interventions.length > 0);
  }, [categories, searchQuery]);

  // Toggle category expansion
  const toggleCategory = (catId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [catId]: !prev[catId]
    }));
  };

  // Check if intervention is selected
  const isSelected = (intervention) => {
    return selected.some(s => s.name === intervention.name);
  };

  // Toggle intervention selection
  const toggleIntervention = (intervention) => {
    if (isSelected(intervention)) {
      setSelected(prev => prev.filter(s => s.name !== intervention.name));
    } else {
      setSelected(prev => [...prev, {
        id: Date.now().toString(),
        name: intervention.name,
        description: intervention.description,
        modality: null,
        frequency: null
      }]);
    }
  };

  // Update intervention settings
  const updateIntervention = (index, field, value) => {
    setSelected(prev => prev.map((item, i) => 
      i === index ? { ...item, [field]: value } : item
    ));
  };

  // Remove intervention
  const removeIntervention = (index) => {
    setSelected(prev => prev.filter((_, i) => i !== index));
  };

  // Add custom intervention
  const addCustomIntervention = () => {
    if (!customName.trim()) {
      toast.error('Please enter an intervention name');
      return;
    }
    
    setSelected(prev => [...prev, {
      id: Date.now().toString(),
      name: customName.trim(),
      description: customDescription.trim() || null,
      modality: customModality || null,
      frequency: customFrequency || null,
      isCustom: true
    }]);
    
    setCustomName('');
    setCustomDescription('');
    setCustomModality('');
    setCustomFrequency('');
    setShowCustomForm(false);
    toast.success('Custom intervention added');
  };

  // Handle save
  const handleSave = () => {
    onSave(selected);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-teal-600" />
            {title}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-hidden flex gap-4">
          {/* Left Panel: Intervention Library */}
          <div className="flex-1 flex flex-col overflow-hidden border rounded-lg">
            {/* Search */}
            <div className="p-3 border-b bg-gray-50">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search interventions..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 h-9"
                />
              </div>
            </div>

            {/* Categories List */}
            <div className="flex-1 overflow-y-auto p-2">
              {loading ? (
                <div className="flex items-center justify-center h-32">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
                </div>
              ) : filteredCategories.length === 0 ? (
                <div className="text-center text-gray-500 py-8">
                  {searchQuery ? 'No interventions match your search' : 'No interventions available'}
                </div>
              ) : (
                <div className="space-y-2">
                  {filteredCategories.map(({ category, interventions }) => (
                    <Collapsible
                      key={category.id}
                      open={expandedCategories[category.id] || searchQuery}
                      onOpenChange={() => !searchQuery && toggleCategory(category.id)}
                    >
                      <CollapsibleTrigger asChild>
                        <div 
                          className="flex items-center justify-between p-2 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors"
                          style={{ borderLeft: `4px solid ${category.color || '#6B7280'}` }}
                        >
                          <div className="flex items-center gap-2">
                            <span className="text-lg">{category.icon}</span>
                            <span className="font-medium text-sm">{category.name}</span>
                            <Badge variant="secondary" className="text-xs">
                              {interventions.length}
                            </Badge>
                          </div>
                          {!searchQuery && (
                            expandedCategories[category.id] 
                              ? <ChevronDown className="h-4 w-4 text-gray-500" />
                              : <ChevronRight className="h-4 w-4 text-gray-500" />
                          )}
                        </div>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <div className="ml-6 mt-1 space-y-1">
                          {interventions.map((intervention) => (
                            <div
                              key={intervention.id}
                              onClick={() => toggleIntervention(intervention)}
                              className={`flex items-start gap-2 p-2 rounded cursor-pointer transition-colors text-sm ${
                                isSelected(intervention) 
                                  ? 'bg-teal-50 border border-teal-200' 
                                  : 'hover:bg-gray-50'
                              }`}
                            >
                              <div className={`mt-0.5 w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${
                                isSelected(intervention) 
                                  ? 'bg-teal-600 border-teal-600' 
                                  : 'border-gray-300'
                              }`}>
                                {isSelected(intervention) && <Check className="h-3 w-3 text-white" />}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="font-medium text-gray-900">{intervention.name}</div>
                                {intervention.description && (
                                  <div className="text-xs text-gray-500 line-clamp-2">{intervention.description}</div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </CollapsibleContent>
                    </Collapsible>
                  ))}
                </div>
              )}
            </div>

            {/* Custom Intervention */}
            <div className="p-3 border-t bg-gray-50">
              {showCustomForm ? (
                <div className="space-y-2">
                  <Input
                    placeholder="Intervention name (e.g., Couples Therapy)..."
                    value={customName}
                    onChange={(e) => setCustomName(e.target.value)}
                    className="h-8 text-sm"
                    autoFocus
                  />
                  <textarea
                    placeholder="Intervention description/text (e.g., During weekly couples therapy clinician will assist couple with improving communication...)..."
                    value={customDescription}
                    onChange={(e) => setCustomDescription(e.target.value)}
                    className="w-full h-20 text-sm p-2 border rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                  <div className="grid grid-cols-2 gap-2">
                    <Select value={customModality} onValueChange={setCustomModality}>
                      <SelectTrigger className="h-8 text-xs">
                        <SelectValue placeholder="Modality (optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        {MODALITIES.map(m => (
                          <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select value={customFrequency} onValueChange={setCustomFrequency}>
                      <SelectTrigger className="h-8 text-xs">
                        <SelectValue placeholder="Frequency (optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        {FREQUENCIES.map(f => (
                          <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={addCustomIntervention} className="flex-1 h-8">
                      <Plus className="h-3 w-3 mr-1" /> Add Custom
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => { setShowCustomForm(false); setCustomName(''); setCustomDescription(''); }} className="h-8">
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="w-full h-8 text-xs"
                  onClick={() => setShowCustomForm(true)}
                >
                  <Plus className="h-3 w-3 mr-1" /> Add Custom Intervention
                </Button>
              )}
            </div>
          </div>

          {/* Right Panel: Selected Interventions */}
          <div className="w-80 flex flex-col border rounded-lg">
            <div className="p-3 border-b bg-gray-50">
              <div className="flex items-center justify-between">
                <Label className="font-medium">Selected ({selected.length})</Label>
                {selected.length > 0 && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="h-6 text-xs text-red-500 hover:text-red-600"
                    onClick={() => setSelected([])}
                  >
                    Clear All
                  </Button>
                )}
              </div>
            </div>
            
            <div className="flex-1 overflow-y-auto p-2">
              {selected.length === 0 ? (
                <div className="text-center text-gray-500 py-8 text-sm">
                  Select interventions from the library
                </div>
              ) : (
                <div className="space-y-2">
                  {selected.map((intervention, index) => (
                    <div 
                      key={intervention.id} 
                      className="p-2 bg-white border rounded-lg shadow-sm"
                    >
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="flex-1 min-w-0">
                          <input
                            type="text"
                            value={intervention.name}
                            onChange={(e) => updateIntervention(index, 'name', e.target.value)}
                            className="font-medium text-sm text-teal-700 w-full bg-transparent border-b border-transparent hover:border-gray-300 focus:border-teal-500 focus:outline-none px-0 py-0.5"
                            data-testid={`intervention-name-${index}`}
                          />
                          <textarea
                            value={intervention.description || ''}
                            onChange={(e) => updateIntervention(index, 'description', e.target.value)}
                            placeholder="Add description / customize intervention text..."
                            className="text-xs text-gray-600 mt-1 w-full bg-transparent border border-transparent hover:border-gray-200 focus:border-teal-400 focus:outline-none rounded px-1 py-0.5 resize-none overflow-hidden"
                            rows={2}
                            onInput={(e) => { e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; }}
                            data-testid={`intervention-desc-${index}`}
                          />
                          {intervention.isCustom && (
                            <Badge variant="outline" className="text-[10px] mt-0.5">Custom</Badge>
                          )}
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-5 w-5 p-0 text-gray-400 hover:text-red-500 flex-shrink-0"
                          onClick={() => removeIntervention(index)}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-1.5">
                        <Select 
                          value={intervention.modality || ''} 
                          onValueChange={(v) => updateIntervention(index, 'modality', v)}
                        >
                          <SelectTrigger className="h-7 text-[11px]">
                            <SelectValue placeholder="Modality" />
                          </SelectTrigger>
                          <SelectContent>
                            {MODALITIES.map(m => (
                              <SelectItem key={m.value} value={m.value} className="text-xs">
                                {m.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        
                        <Select 
                          value={intervention.frequency || ''} 
                          onValueChange={(v) => updateIntervention(index, 'frequency', v)}
                        >
                          <SelectTrigger className="h-7 text-[11px]">
                            <SelectValue placeholder="Frequency" />
                          </SelectTrigger>
                          <SelectContent>
                            {FREQUENCIES.map(f => (
                              <SelectItem key={f.value} value={f.value} className="text-xs">
                                {f.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <DialogFooter className="border-t pt-4 mt-4">
          <DialogClose asChild>
            <Button variant="outline">Cancel</Button>
          </DialogClose>
          <Button onClick={handleSave} className="bg-teal-600 hover:bg-teal-700">
            Save {selected.length > 0 && `(${selected.length})`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default InterventionLibrarySelector;
